import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Bell, BellOff, BellRing } from "lucide-react";
import { useTr } from "@/lib/tr";

const b64ToU8 = (s) => { const p = "=".repeat((4 - (s.length % 4)) % 4); const raw = atob((s + p).replace(/-/g, "+").replace(/_/g, "/")); return Uint8Array.from([...raw].map((c) => c.charCodeAt(0))); };
const supported = () => typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

// Device notifications for staff: explicit permission request, real Web Push subscription.
export function PushToggle() {
  const tr = useTr();
  const [state, setState] = useState("loading");
  useEffect(() => {
    if (!supported()) { setState("unsupported"); return; }
    if (Notification.permission === "denied") { setState("blocked"); return; }
    navigator.serviceWorker.getRegistration("/sw-push.js").then(async (reg) => {
      const sub = reg && (await reg.pushManager.getSubscription());
      setState(sub && Notification.permission === "granted" ? "on" : "off");
    }).catch(() => setState("off"));
  }, []);
  const enable = async () => {
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") { setState(perm === "denied" ? "blocked" : "off"); return; }
      const { data } = await api.get("/push/config");
      const reg = await navigator.serviceWorker.register("/sw-push.js");
      await navigator.serviceWorker.ready;
      const sub = (await reg.pushManager.getSubscription()) || (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64ToU8(data.public_key) }));
      await api.post("/push/subscribe", sub.toJSON());
      await api.put("/push/prefs", { enabled: true, preview: false });
      setState("on");
      toast.success(tr("Notifications activées sur cet appareil", "Notifications enabled on this device"));
    } catch { toast.error(tr("Impossible d'activer les notifications sur cet appareil", "Could not enable notifications on this device")); }
  };
  const disable = async () => {
    const reg = await navigator.serviceWorker.getRegistration("/sw-push.js");
    const sub = reg && (await reg.pushManager.getSubscription());
    if (sub) { await api.post("/push/unsubscribe", sub.toJSON()).catch(() => {}); await sub.unsubscribe(); }
    setState("off");
  };
  if (state === "loading" || state === "unsupported") return null;
  const label = { on: tr("Notifications activées", "Notifications on"), off: tr("Activer les notifications", "Enable notifications"), blocked: tr("Notifications bloquées par le navigateur", "Notifications blocked by browser") }[state];
  const Icon = state === "on" ? BellRing : state === "blocked" ? BellOff : Bell;
  return (
    <button type="button" onClick={state === "on" ? disable : state === "off" ? enable : undefined} title={label} data-testid="push-toggle" data-state={state}
      className={`flex h-9 items-center gap-1.5 rounded-full border px-3 text-xs transition-colors ${state === "on" ? "border-emerald-600/50 text-emerald-400" : state === "blocked" ? "border-red-600/40 text-red-400" : "border-slate-700 text-slate-300 hover:bg-slate-800"}`}>
      <Icon className="h-4 w-4" /><span className="hidden md:inline">{label}</span>
    </button>
  );
}
